<template>
  <div
    class="note"
    :class="{ 'note--readonly': readonly }"
  >
    <button
      v-if="readonly"
      class="note__control-btn note__control-btn--trash note__control-btn--readonly"
      @click.stop="onDelete"
    />

    <div class="note__head">
      <div
        v-if="!readonly"
        class="note__control"
      >
        <button
          class="note__control-btn note__control-btn--back"
          @click="onBack"
        />
        <button
          class="note__control-btn note__control-btn--undo"
          @click="onUndo"
          :disabled="!newData"
        />
        <button
          class="note__control-btn note__control-btn--redo"
          @click="onRedo"
          :disabled="!snapshotIsCreated"
        />
        <button
          v-if="!noDeleteBtn"
          class="note__control-btn note__control-btn--trash"
          @click="onDelete"
        />
        <button
          @click="onSave"
          class="note__btn-save"
          :disabled="!newData"
        >
          Save
        </button>
      </div>

      <span
        v-if="readonly"
        class="note__title"
      >
        {{ innerTitle }}
      </span>
      <input
        v-else
        class="note__title"
        :class="{ 'note__title--input': !readonly }"
        type="text" v-model="innerTitle"
        placeholder="Note title"
      >
    </div>

    <div class="note__body">
      <todo-list
        :todos="innerTodos"
        :readonly="readonly"
        @todo-text-input="onTodoTextInput"
        @todo-delete="onTodoDelete"
        @todo-change-completed="onTodoChangeCompleted"
        @todo-add="onTodoAdd"
      />
    </div>
  </div>
</template>

<script>
import TodoList from '@/components/TodoList'
import clone from 'clone-deep'
import isEqual from 'lodash.isequal'
import isEmpty from 'lodash.isempty'

const COUNT_TODO_FOR_PREVIEW = 3

export default {
  name: 'Note',

  components: {
    TodoList
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    todos: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    noDeleteBtn: {
      type: Boolean,
      default: false
    }
  },

  data: vm => ({
    innerTitle: vm.title,
    innerTodos: clone(vm.readonly ? vm.todos.slice(0, COUNT_TODO_FOR_PREVIEW) : vm.todos),
    snapshot: {},
    newData: false,
    initNote: {
      title: vm.title,
      todos: clone(vm.todos)
    }
  }),

  computed: {
    note: {
      get() {
        return {
          title: this.innerTitle,
          todos: this.innerTodos
        }
      },
      set(value) {
        this.innerTitle = value.title
        this.innerTodos = value.todos
      }
    },

    snapshotIsCreated() {
      return isEmpty(this.snapshot) === false && !this.newData
    }
  },

  methods: {
    onBack() {
      this.$emit('back', this.newData)
    },

    onSave() {
      this.$emit('save', this.note)
      this.initNote = clone(this.note)
      this.newData = false
    },

    onDelete() {
      this.$emit('delete')
    },

    onTodoAdd() {
      this.innerTodos.unshift({ id: new Date().valueOf(), completed: false, text: '' })
    },

    onTodoTextInput({ value, id }) {
      this.innerTodos[this.getTodoIndex(id)].text = value
    },

    onTodoDelete(id) {
      this.innerTodos.splice(this.getTodoIndex(id), 1)
    },

    onTodoChangeCompleted({ value, id }) {
      this.innerTodos[this.getTodoIndex(id)].completed = value
    },

    getTodoIndex(id) {
      return this.innerTodos.findIndex(todo => todo.id == id)
    },

    onUndo() {
      this.snapshot = clone(this.note)
      this.note = clone(this.initNote)
    },

    onRedo() {
      this.note = clone(this.snapshot)
      this.snapshot = {}
    }
  },

  watch: {
    note: {
      handler(val) {
        this.newData = !isEqual(val, this.initNote)
      },
      deep: true
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/css/vars.styl'
@import '~@/css/mixins.styl'

$control-button = {
  undo: undo,
  redo: redo,
  back: back,
  trash: trash
}

$icon-path = '~@/assets/icons/'

.note
  background-color $light
  border-radius 5px
  padding 24px
  box-shadow $elevation
  transition $transition
  width 100%
  position relative

  &--readonly
    cursor pointer
    user-select none
    hover()

  &__control
    display flex
    justify-content flex-end
    margin-bottom 16px

  &__control-btn
    control_button()

    for $val, $key in $control-button
      &--{$key}
        background-image url($icon-path + $val + '_w.svg')
        &:disabled
          background-image url($icon-path + $val + '.svg')

    &--back
      margin-right auto

    &--undo
      margin-right 4px

    &--trash
      margin-left 16px

    &--readonly
      position absolute
      right 16px
      top 16px
      &:hover
        background-image: url($icon-path + $control-button.trash + '_r.svg')

  &__btn-save
    button()
    margin-left 16px

  &__title
    display inline-block
    font-size 25px
    margin-bottom 16px
    font-family 'Quicksand', sans-serif
    width 100%
    padding-right 50px

    &--input
      border-bottom 1px solid $primary + 25%
      margin-bottom 30px
      padding-right 0

      &:focus
        border-color $white
</style>
